import SridSelector from '@/components/SridSelector';
import { Button } from '@/components/ui/button';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { createProjectMutation } from '@/queries/project.query';
import { useFormik } from 'formik';
import { analytics } from '@/lib/analytics';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ProjectCreateModal: FunctionComponent<{ onSuccess: (projectId?: string) => void; organizationId: string }> = ({
  onSuccess,
  organizationId,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const formik = useFormik<{ name: string; srid: number }>({
    initialValues: {
      name: '',
      srid: 4326,
    },
    onSubmit: async (values) => {
      if (!organizationId) return;
      const toastId = toast.loading(t('projectCreate.creating'));
      const res = await execute(createProjectMutation, {
        input: { name: values.name, organizationId, srid: values.srid },
      });
      toast.success(t('projectCreate.created'), { id: toastId });
      navigate(`/projects/${res.createProject.id}`);
      onSuccess(res.createProject.id);
      analytics.capture('Project Created');
    },
  });

  return (
    <DialogContent className='sm:max-w-[520px]'>
      <DialogHeader>
        <DialogTitle>{t('projectCreate.title')}</DialogTitle>
        <DialogDescription>{t('projectCreate.description')}</DialogDescription>
      </DialogHeader>
      <div className='grid gap-4'>
        <div className='grid gap-3'>
          <Label htmlFor='name-1'>{t('projectCreate.name')}</Label>
          <Input
            id='name-1'
            name='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            placeholder={t('projectCreate.namePlaceholder')}
          />
        </div>
        <div className='grid gap-3'>
          <Label>{t('projectCreate.epsg')}</Label>
          <SridSelector
            className='w-full'
            value={formik.values.srid.toString()}
            onChange={(value) => formik.setFieldValue('srid', parseInt(value))}
          />
        </div>
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant='outline'>{t('projectCreate.cancel')}</Button>
        </DialogClose>
        <DialogClose asChild>
          <Button type='submit' onClick={formik.submitForm} disabled={formik.isSubmitting || !formik.dirty}>
            {t('projectCreate.save')}
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};

export default ProjectCreateModal;
